// Shared types for slot machine settings — used by both frontend and backend
// DO NOT import mongoose here — this file must be browser-safe

// Interface for a single outcome type's probability
export interface IOutcomeType {
  name: string
  probability: number  // 0-100, all enabled must sum to 100
  enabled: boolean
}

// Interface for a denomination within an outcome range
export interface IDenomination {
  value: number | string  // number for Points/Bonus/FreeSpins, string for Merchandise/Quest
  probability: number     // 0-100, must sum to 100 within each outcome type
  label: string           // Display label (e.g., "Oval T-Shirt", "Submit 3 deals")
  metadata?: Record<string, unknown>  // Extra data (quest rules, item details)
}

// Interface for outcome ranges per type
export interface IOutcomeRanges {
  points: IDenomination[]
  bonus: IDenomination[]
  merchandise: IDenomination[]
  freeSpins: IDenomination[]
  quest: IDenomination[]
  miss: IDenomination[]
}

export type SlotSettingsScope = "system" | "agency" | "user"

// Default outcome types (must sum to 100)
export const getDefaultOutcomeTypes = (): IOutcomeType[] => [
  { name: "points", probability: 40, enabled: true },
  { name: "bonus", probability: 15, enabled: true },
  { name: "merchandise", probability: 5, enabled: true },
  { name: "freeSpins", probability: 10, enabled: true },
  { name: "quest", probability: 10, enabled: true },
  { name: "miss", probability: 20, enabled: true },
]

// Default outcome ranges
export const getDefaultOutcomeRanges = (): IOutcomeRanges => ({
  points: [
    { value: 1, probability: 30, label: "1 Point" },
    { value: 2, probability: 25, label: "2 Points" },
    { value: 5, probability: 20, label: "5 Points" },
    { value: 10, probability: 12, label: "10 Points" },
    { value: 20, probability: 7, label: "20 Points" },
    { value: 50, probability: 4, label: "50 Points" },
    { value: 75, probability: 1.5, label: "75 Points" },
    { value: 100, probability: 0.5, label: "100 Points" },
  ],
  bonus: [
    { value: 1, probability: 40, label: "$1 Bonus" },
    { value: 2, probability: 30, label: "$2 Bonus" },
    { value: 5, probability: 20, label: "$5 Bonus" },
    { value: 10, probability: 8, label: "$10 Bonus" },
    { value: 25, probability: 2, label: "$25 Bonus" },
  ],
  merchandise: [
    { value: "tshirt", probability: 40, label: "Oval T-Shirt", metadata: { sku: "OVAL-TSHIRT-001" } },
    { value: "hat", probability: 30, label: "Oval Hat", metadata: { sku: "OVAL-HAT-001" } },
    { value: "bottle", probability: 20, label: "Oval Water Bottle", metadata: { sku: "OVAL-BOTTLE-001" } },
    { value: "ticket", probability: 10, label: "Event Ticket", metadata: { sku: "OVAL-TICKET-001" } },
  ],
  freeSpins: [
    { value: 1, probability: 60, label: "1 Free Spin" },
    { value: 2, probability: 30, label: "2 Free Spins" },
    { value: 3, probability: 10, label: "3 Free Spins" },
  ],
  quest: [
    { value: "easy", probability: 50, label: "Easy Quest", metadata: { tier: "easy", targetRange: [1, 3] } },
    { value: "medium", probability: 35, label: "Medium Quest", metadata: { tier: "medium", targetRange: [3, 5] } },
    { value: "hard", probability: 15, label: "Hard Quest", metadata: { tier: "hard", targetRange: [5, 10] } },
  ],
  miss: [
    { value: "bust", probability: 100, label: "Better luck next time!" },
  ],
})

// Validation helpers
export const validateOutcomeTypeProbabilities = (outcomeTypes: IOutcomeType[]): boolean => {
  const enabledTypes = outcomeTypes.filter(t => t.enabled)
  const sum = enabledTypes.reduce((acc, t) => acc + t.probability, 0)
  return Math.abs(sum - 100) < 0.01
}

export const validateDenominationProbabilities = (denominations: IDenomination[]): boolean => {
  if (denominations.length === 0) return true
  const sum = denominations.reduce((acc, d) => acc + d.probability, 0)
  return Math.abs(sum - 100) < 0.01
}
